import { PositionClock } from "../app/clock";
import { MusicalTimeline } from "../music/musical-timeline";

/**
 * L'origine du temps musical, partagée par tout ce qui joue.
 *
 * # Trois grandeurs, et une seule qui bouge
 *
 * `startedAt` est un **fait historique** : l'instant réel où ce transport a été
 * posé. Il ne bouge jamais, et ne sert qu'à répondre « on joue depuis douze
 * minutes ».
 *
 * `origin` est une **constante de conversion** entre l'horloge et la position :
 * `elapsedTime = clock − origin`. Elle glisse — à la pose d'une session, et à
 * la reprise après une pause. Les deux étaient confondues, et c'est ce qui
 * rendait la première trompeuse.
 *
 * `frozenAt` est la position gelée pendant la pause, `undefined` sinon.
 *
 * # La pause fait glisser l'origine
 *
 * Plutôt que d'accumuler un temps de pause à retrancher — un compteur de plus,
 * qu'il faut penser à mettre à jour partout —, la reprise repose l'origine de
 * façon que la position reprenne exactement là où elle s'était arrêtée. Un seul
 * état, idempotent, et l'invariant se lit dans le code : `elapsedTime` est
 * continue de part et d'autre de la pause.
 *
 * # Poser une origine, pas un décalage
 *
 * L'API ne prend jamais « de combien décaler » mais « où commence cette
 * session » (`setOrigin`) ou « où elle en est » (`setPosition`). Le transport
 * fait la soustraction ; l'appelant n'a pas à se demander dans quel sens va le
 * signe — c'est exactement l'erreur qu'un décalage invitait à commettre.
 *
 * Le transport ne connaît rien des sessions : ce sont elles qui écrivent leur
 * état ici en entrant en scène, et le relisent en sortant. C'est ce qui permet
 * qu'il n'y en ait qu'un, tenu par tous les `Scheduler` et tous les
 * `LocalTransport`, et qui survive aux bascules.
 */
export class Transport {
  /** Fait historique : l'instant du début. Ne bouge jamais. */
  private readonly startedAt: number;

  /** Constante de conversion horloge → position. Glisse. */
  private origin: number;

  /** Position gelée pendant la pause, `undefined` quand ça tourne. */
  private frozenAt?: number;

  constructor(public readonly clock: PositionClock, public timeline: MusicalTimeline) {
    this.startedAt = clock.currentTime;
    this.origin = this.startedAt;
  }

  currentTick() {
    return this.timeline.secondsToTick(this.elapsedTime()) ?? 0;
  }

  elapsedTime() {
    return this.frozenAt ?? this.clock.currentTime - this.origin;
  }

  /**
   * La position à un instant donné de l'horloge, en secondes.
   *
   * Pour dater un geste dont la source connaît l'instant mieux que nous : un
   * message MIDI est horodaté à sa réception par le navigateur, avant que le
   * fil principal soit libre pour le traiter. En pause la position est gelée,
   * et ne dépend d'aucun instant.
   */
  elapsedTimeAt(clockTime: number) {
    return this.frozenAt ?? clockTime - this.origin;
  }

  /** L'instant réel du début, sur l'horloge de position. Pour l'affichage. */
  getStartedAt() {
    return this.startedAt;
  }

  /** L'origine effective : c'est elle qui fait foi pour toute conversion. */
  getOrigin() {
    return this.origin;
  }

  /**
   * « Cette session a commencé là. »
   *
   * Absolu et non cumulatif : l'appelant tient l'origine qu'il connaît, et la
   * réaffirme. Sans effet sur une pause en cours — la position gelée reste
   * gelée, et c'est `resume()` qui recalculera l'origine à partir d'elle.
   */
  setOrigin(clockTime: number) {
    this.origin = clockTime;
  }

  /** « Cette session en est là, maintenant. » Le transport fait la soustraction. */
  setPosition(seconds: number) {
    this.origin = this.clock.currentTime - seconds;

    if (this.frozenAt !== undefined) {
      this.frozenAt = seconds;
    }
  }

  /**
   * Gèle la position. Idempotent : une seconde pause ne déplace rien, puisque
   * `elapsedTime()` rend déjà la valeur gelée.
   */
  pause() {
    this.frozenAt = this.elapsedTime();
  }

  /**
   * Repart de la position gelée, en faisant glisser l'origine d'autant que la
   * pause a duré. Sans effet si rien n'était gelé.
   */
  resume() {
    if (this.frozenAt === undefined) {
      return;
    }

    this.origin = this.clock.currentTime - this.frozenAt;
    this.frozenAt = undefined;
  }

  get isPaused() {
    return this.frozenAt !== undefined;
  }

  setMusicalTimeline(timeline: MusicalTimeline) {
    this.timeline = timeline;
  }
}
